import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { InfoModalComponent, InfoModalConfig, InfoModalType } from '../components/info-modal/info-modal.component';
import { AlertModalComponent } from '../components/alert-modal/alert-modal/alert-modal.component';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class InfoModalService {
  /** Sync gate so parallel showContactErrorModal / showAlertModal calls do not stack. */
  private modalOpenOrOpening = false;

  constructor(private modalController: ModalController, private router: Router) {}

  /**
   * Show an info modal with the provided configuration
   */
  async showModal(config: InfoModalConfig): Promise<HTMLIonModalElement> {
    const modal = await this.modalController.create({
      component: InfoModalComponent,
      htmlAttributes: { 'aria-label': config.title || config.type },
      componentProps: {
        config: {
          dismissOnBackdrop: true,
          showCloseButton: true,
          ...config
        }
      },
      backdropDismiss: config.dismissOnBackdrop !== false,
      cssClass: `info-modal info-modal--${config.type}`
    });

    await modal.present();
    return modal;
  }


  async showAlertModal(message: string, title?: string): Promise<HTMLIonModalElement | null> {
    // Claim gate before any await so parallel callers cannot both create.
    if (this.modalOpenOrOpening) {
      return (await this.modalController.getTop()) ?? null;
    }
    this.modalOpenOrOpening = true;

    try {
      const existing = await this.modalController.getTop();
      if (existing) {
        this.modalOpenOrOpening = true; // keep gate while top modal owns the UI
        void existing.onDidDismiss?.().then(() => {
          this.modalOpenOrOpening = false;
        });
        return existing;
      }

      const modal = await this.modalController.create({
        component: AlertModalComponent,
        htmlAttributes: { 'aria-label': title },
        componentProps: {
          title,
          message,
          primaryButtonText: 'App Support',
        },
        cssClass: 'ion-alert-modal',
        backdropDismiss: true,
      });

      await modal.present();
      void modal.onDidDismiss().then(() => {
        this.modalOpenOrOpening = false;
      });
      return modal;
    } catch (err) {
      this.modalOpenOrOpening = false;
      throw err;
    }
  }

  /**
   * Show an info modal with simple message
   */
  async showInfo(message: string, title?: string): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'info',
      title,
      message,
      primaryButton: {
        text: 'OK'
      }
    });
  }

  /**
   * Show a warning modal with simple message
   */
  async showWarning(message: string, title?: string): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'warning',
      title,
      message,
      primaryButton: {
        text: 'OK'
      }
    });
  }

  /**
   * Show an error modal with simple message
   */
  async showError(message: string, title?: string): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'error',
      title,
      message,
      primaryButton: {
        text: 'OK'
      }
    });
  }

  /**
   * Show a confirmation modal with primary and secondary actions
   */
  async showConfirmation(
    message: string,
    primaryText: string = 'Confirm',
    secondaryText: string = 'Cancel',
    type: InfoModalType = 'warning',
    title?: string
  ): Promise<HTMLIonModalElement> {
    return this.showModal({
      type,
      title,
      message,
      primaryButton: {
        text: primaryText
      },
      secondaryButton: {
        text: secondaryText
      },
      dismissOnBackdrop: false,
      showCloseButton: false
    });
  }

  /**
   * Show NHS account exists notification
   */
  async showNhsAccountExists(email: string): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'info',
      title: 'Account Already Exists',
      message: `You already have an account registered with NHS email: <strong>${email}</strong>. Please use the login page to access your account.`,
      primaryButton: {
        text: 'Go to Login'
      },
      secondaryButton: {
        text: 'Use Different Method'
      },
      dismissOnBackdrop: false
    });
  }

  /**
   * Show age restriction error
   */
  async showAgeRestrictionError(minimumAge: number): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'error',
      title: 'Age Requirement',
      message: `You must be at least ${minimumAge} years old to create an account. Please check your date of birth and try again.`,
      primaryButton: {
        text: 'OK'
      }
    });
  }

  /**
   * Show password reset success
   */
  async showPasswordResetSuccess(): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'info',
      title: 'Password Reset Successful',
      message: 'Your password has been successfully reset. You can now log in with your new password.',
      primaryButton: {
        text: 'Continue to Login'
      }
    });
  }

  /**
   * Show registration success
   */
  async showRegistrationSuccess(): Promise<HTMLIonModalElement> {
    return this.showModal({
      type: 'info',
      title: 'Registration Successful',
      message: 'Your account has been successfully created. Welcome to GetUBetter!',
      primaryButton: {
        text: 'Get Started'
      }
    });
  }

  /**
   * Show contact app support modal for internal errors
   * This provides a standardized way to show errors and direct users to support.
   * Deduped via showAlertModal (skips create when a modal is open or presenting).
   */
  async showContactErrorModal(customMessage?: string, customTitle?: string): Promise<HTMLIonModalElement | null> {
    // Sync gate first — parallel HTTP error handlers must not both pass getTop().
    if (this.modalOpenOrOpening) {
      return (await this.modalController.getTop()) ?? null;
    }

    const title = customTitle || 'Something went wrong';
    const message = customMessage || 'Please contact App Support for assistance or try again later.';

    const modal = await this.showAlertModal(message, title);
    if (modal) {
      const { data, role } = await modal.onWillDismiss();
      // Handle any actions after dismissal if needed
      // if dismissed via primary button, navigate to support page
      if (role === 'primary') {
        // Navigate to need-help page
        // Assuming you have a router service available
        this.router.navigate(['/need-help']);
      }
    }
    return modal;
  }

  /**
   * Dismiss all open modals
   */
  async dismissAll(): Promise<void> {
    const topModal = await this.modalController.getTop();
    if (topModal) {
      await this.modalController.dismiss();
    }
  }
}
